<script setup>
const { transitionColour } = useTailwindConfig();
const { currentRoute } = useRouter();
const client = useSupabaseClient();
const user = useState('user');
const municipality = useCookie('municipality');
const loading = ref(false);

const openFeedbackModal = useState('openFeedbackModal');

const useNonAdminView = useCookie('useNonAdminView');
if (useNonAdminView.value == null) {
  useNonAdminView.value = false;
}

const usePlannerView = useCookie('usePlannerView');
if (usePlannerView.value == null) {
  usePlannerView.value = true;
}

const supportedMunicipalityLogos = [
  'mm',
  'crowsnest',
];

const useMunicipalityLogo = computed(() => {
  if (user.value?.subscription != 'builder' && user.value?.use_municipality_branding && municipality.value && supportedMunicipalityLogos.includes(municipality.value)) {
    return true;
  }
  return false;
});

// Sign out handler
async function signOut() {
  loading.value = true;
  const { error } = await client.auth.signOut();

  // Delete the profileData cookie too (the one we set in default.vue to save on API calls)
  const profileDataCookie = useCookie('profileData');
  profileDataCookie.value = null;

  if (error) {
    console.log('There was an error signing out: ' + error.error_description || error.message);
  }
  else {
    // Using navigateTo('/') doesn't work sometimes for some reason
    window.location.href = '/';
  }
}

function closeDropdownAndNavigateTo(url, dropdownId, event) {
  // Close the dropdown before navigating
  const dropdown = document.getElementById(dropdownId);
  dropdown.click();

  // If ctrl key is pressed, open in new tab
  if (event?.ctrlKey) {
    window.open(url, '_blank');
  } else {
    navigateTo(url);
  }
}
</script>

<template>
  <nav class="flex items-center bg-gray-100 sticky z-50 top-0 h-[70px] border-b border-gray-200">
    <div class="w-full px-3 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <div class="w-[800px] items-center flex space-x-4">
          <!-- Logo -->
          <NuxtLink to="/" class="p-0.5 hover:bg-gray-200 rounded-lg" :class="transitionColour">
            <!-- Siteability Logo -->
            <div class="h-14">
              <NuxtImg v-if="useMunicipalityLogo" :src="`/images/municipality-logos/${municipality}.png`"
                alt="Siteability" class="w-full h-full object-contain" />
              <NuxtImg v-else src="/images/logo.png" alt="Siteability" class="w-full h-full object-contain" />
            </div>
          </NuxtLink>

          <!-- Homeowner View Toggle-->
          <!-- Needs to be ClientOnly to prevent some hydration errors for some reason -->
          <ClientOnly>
            <UButton v-if="user?.subscription == 'planner'" variant="soft" color="neutral"
              @click="usePlannerView = !usePlannerView;" size="lg">
              <span>Homeowner View</span>
              <USwitch :model-value="!usePlannerView" variant="outline" color="primary" />
            </UButton>
          </ClientOnly>
        </div>

        <!-- Search Bar (only show on report pages or sample user) -->
        <SearchBar v-if="user && (currentRoute.path.includes('report/') || showPlannerView())" type="navbar"
          class="hidden md:flex grow px-4 max-w-96" />

        <!-- Right side buttons (logged in) -->
        <div v-if="user" class="w-[800px] flex justify-end items-center divide-x divide-gray-700">
          <!-- Metric/Imperial Toggle -->
          <UnitSwitcher v-if="currentRoute.path.includes('report/')" />

          <!-- Inquire to City (homeowner only) -->
          <UButton v-if="!showPlannerView() && !showAdminView() && user?.subscription != 'builder'"
            class="mx-2 text-nowrap" size="lg" to="/inquire" icon="material-symbols:chat-outline-rounded">
            Inquire to City
          </UButton>

          <!-- Feedback & Bugs -->
          <UButton @click="openFeedbackModal.open()" class="mx-2 text-nowrap" size="lg"
            icon="material-symbols:bug-report-outline-rounded">
            Feedback & Bugs
          </UButton>

          <div class="pl-4 relative text-left flex my-auto">
            <UPopover mode="click" :content="{ side: 'bottom', align: 'start' }" :ui="{ content: 'rounded-lg' }">
              <button type="button" id="dropdown-button"
                class="sm:mr-3 text-gray-900 flex items-center hover:bg-gray-200 rounded-lg p-1"
                :class="transitionColour">
                <UIcon name="material-symbols:person-outline" size="29" />
              </button>

              <template #content>
                <div class="text-lg text-black p-4 shadow-sm font-medium" :class="transitionColour">
                  <div class="flex justify-between items-start ">
                    <!-- Name -->
                    <span class="block text-sm text-black-900">
                      {{ user?.first_name }} {{ user?.last_name }}
                    </span>

                    <!-- Is Demo Badge -->
                    <UBadge v-if="user?.is_demo" label="DEMO" size="sm" color="error" />
                  </div>
                  <!-- Email -->
                  <span class="block text-sm text-gray-500 truncate">
                    {{ user?.email }}
                  </span>
                </div>
                <div class="rounded-b-lg text-sm divide-gray-300">
                  <hr class="border-gray-300" />
                  <button @click="(e) => closeDropdownAndNavigateTo('/', 'dropdown-button', e)"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="currentRoute.path == '/' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                    <UIcon name="material-symbols:search" class="h-5 w-5 mr-1" />
                    New Search
                  </button>

                  <button @click="(e) => closeDropdownAndNavigateTo('/search-history', 'dropdown-button', e)"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="currentRoute.path == '/search-history' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                    <UIcon name="material-symbols:history" class="h-5 w-5 mr-1" />
                    Search History
                  </button>

                  <hr class="border-gray-300" />
                  <button @click="(e) => closeDropdownAndNavigateTo('/users/settings', 'dropdown-button', e)"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="currentRoute.path == '/users/settings' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                    <UIcon name="material-symbols:settings" class="h-5 w-5 mr-1" />
                    Settings
                  </button>

                  <hr class="border-gray-300" />
                  <button @click="openFeedbackModal.open()"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="transitionColour">
                    <UIcon name="material-symbols:bug-report-outline-rounded" class="h-5 w-5 mr-1" />
                    Feedback & Bugs
                  </button>

                  <!-- Admin Only -->
                  <template v-if="user.subscription == 'admin'">
                    <hr class="border-gray-300" />
                    <div class="flex items-center pt-2 pl-2 pb-1 text-gray-400 font-medium">
                      <UIcon name="material-symbols:admin-panel-settings-outline-rounded" class="h-5 w-5 mr-1" />
                      <h4 class="text-sm">Admin Tools</h4>
                    </div>
                    <button @click="(e) => closeDropdownAndNavigateTo('/zoning-editor', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/zoning-editor' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Zoning Editor
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/flags-editor', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/flags-editor' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Flags Editor
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/modules-editor', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/modules-editor' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Modules Editor
                    </button>

                    <button
                      @click="(e) => closeDropdownAndNavigateTo('/zoning-editor/reverse-search', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/zoning-editor/reverse-search' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Reverse Search
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/users', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/users' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Users
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/blog', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/blog' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Blog
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/feedback', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/feedback' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Feedback Tracker
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/demo', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/demo' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Demo
                    </button>

                    <button @click="useNonAdminView = !useNonAdminView;"
                      class="flex items-center justify-between w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="transitionColour">
                      <span>Admin View</span>
                      <USwitch :model-value="!useNonAdminView" />
                    </button>
                  </template>

                  <!--Sign Out-->
                  <hr class="border-gray-300" />
                  <button
                    class="flex justify-between gap-2 w-full px-3 py-2 text-sm rounded-b-lg hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="transitionColour" @click="signOut">
                    <div class="flex items-center">
                      <UIcon name="material-symbols:logout" class="h-5 w-5 mr-1" />
                      Sign out
                    </div>
                  </button>
                </div>
              </template>
            </UPopover>
          </div>
        </div>
      </div>
    </div>

    <!-- Absolutely Positioned Loading Spinner -->
    <div v-if="loading" class="fixed inset-0 bg-black/20 z-49 flex justify-center items-center">
      <LoadingSpinner class="w-14 h-14" />
    </div>
  </nav>
</template>